import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { allComics, categories, getMockChapters } from '@/mocks/comics';
import type { Chapter, Comic } from '@/mocks/types';

const CHAPTERS_PER_PAGE = 30;

function formatViews(views: number) {
  if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showAllDesc, setShowAllDesc] = useState(false);
  const [chapterPage, setChapterPage] = useState(1);

  const comic: Comic | undefined = allComics.find((c) => c.id === id);
  const chapters: Chapter[] = comic ? getMockChapters(comic.id) : [];
  const totalChapterPages = Math.max(1, Math.ceil(chapters.length / CHAPTERS_PER_PAGE));
  const pagedChapters = chapters.slice(
    (chapterPage - 1) * CHAPTERS_PER_PAGE,
    chapterPage * CHAPTERS_PER_PAGE
  );

  const categoryName = comic ? categories.find((c) => c.slug === comic.category)?.name : '';

  const relatedComics = comic
    ? allComics.filter((c) => c.id !== comic.id && c.category === comic.category).slice(0, 6)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  // SEO
  useEffect(() => {
    if (!comic) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 韩漫网站`,
      author: { '@type': 'Person', name: comic.author },
      datePublished: '2024-01-01',
      dateModified: comic.updateTime,
      description: comic.description,
      url: `https://koreanmanhwa.site/comic/${comic.id}`,
      publisher: {
        '@type': 'Organization',
        name: '韩漫网站',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://koreanmanhwa.site/' },
          { '@type': 'ListItem', position: 2, name: `${categoryName}韩漫`, item: `https://koreanmanhwa.site/category/${comic.category}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `https://koreanmanhwa.site/comic/${comic.id}` },
        ],
      },
    };

    const existing = document.querySelector('script[data-seo="comic"]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'comic');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    document.title = `${comic.title}_免费在线阅读_韩漫网站`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `${comic.title} - ${comic.description.slice(0, 120)}`);
    }

    return () => {
      const el = document.querySelector('script[data-seo="comic"]');
      if (el) el.remove();
    };
  }, [comic, categoryName]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">漫画未找到</h1>
            <p className="text-foreground-500 mt-2 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
              {categoryName}韩漫
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-800 font-medium line-clamp-1">{comic.title}</span>
          </nav>
        </div>

        {/* Hero info section */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-8 pb-10">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-[200px] h-[280px] md:w-[240px] md:h-[336px] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="text-sm text-foreground-500">作者：<strong>{comic.author}</strong></span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span className="text-sm text-accent-600 font-semibold">
                  <i className="ri-star-fill text-sm mr-0.5" />
                  {comic.rating}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line text-sm mr-0.5" />
                  {formatViews(comic.views)} 阅读
                </span>
              </div>

              {/* Tags */}
              <div className="flex items-center gap-1.5 flex-wrap mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className={`text-sm text-foreground-600 leading-relaxed ${showAllDesc ? '' : 'line-clamp-3'}`}>
                  {comic.description}
                </p>
                {comic.description.length > 100 && (
                  <button
                    onClick={() => setShowAllDesc(!showAllDesc)}
                    className="text-sm text-primary-500 hover:text-primary-600 mt-1 cursor-pointer whitespace-nowrap"
                  >
                    {showAllDesc ? '收起' : '展开全部'}
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <a
                  href="#chapters"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg" />
                  开始阅读
                </a>
                <button className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer" aria-label="收藏">
                  <i className="ri-heart-line text-lg" />
                </button>
                <button className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer" aria-label="分享">
                  <i className="ri-share-line text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chapter list */}
        <div id="chapters" className="bg-background-100 py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  章节目录
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {chapters.length} 话 · 最近更新 {chapters[chapters.length - 1]?.updateTime || comic.updateTime}
                </p>
              </div>
              <button className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-sort-desc" />
                倒序
              </button>
            </div>

            {/* Chapter grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {pagedChapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 group cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                    {chapter.updateTime}
                  </span>
                </a>
              ))}
            </div>

            {/* Chapter pagination */}
            {totalChapterPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setChapterPage((p) => Math.max(1, p - 1))}
                  disabled={chapterPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line text-sm" />
                </button>
                {Array.from({ length: totalChapterPages }, (_, i) => i + 1).slice(
                  Math.max(0, Math.min(chapterPage - 3, totalChapterPages - 5)),
                  Math.min(totalChapterPages, Math.max(5, chapterPage + 2))
                ).map((page) => (
                  <button
                    key={page}
                    onClick={() => setChapterPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                      chapterPage === page
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setChapterPage((p) => Math.min(totalChapterPages, p + 1))}
                  disabled={chapterPage === totalChapterPages}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line text-sm" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Related comics */}
        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  更多{categoryName}韩漫推荐
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <div key={rc.id} className="flex justify-center">
                    <ComicCard comic={rc} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}